/**
 * Money formatting for the billing UI.
 *
 * The CHARGED-money half of the two money systems in this codebase. Keep it
 * distinct from `formatMoney(microdollars, {currency, rate})` in
 * `@papan/types`' budget module: that one converts USD LLM metering into an
 * admin-chosen DISPLAY currency at a manual multiplier. These amounts are
 * already denominated in the charged currency. Running them through a
 * display-rate multiplier would convert them twice.
 *
 * The currency-aware primitives themselves now live in
 * `shared/types/src/currency.ts` and are re-exported here, because the SERVER
 * needs the same minor-unit rules (the checkout route formats the bounds it
 * quotes, and the catalog sync validates amounts before minting a Price). Two
 * implementations of "how many minor units are in a yen" is exactly the kind
 * of drift that produces a 100× charge, so there is one.
 */

/**
 * Minor units → "RM 49.00" / "$29.00" / "¥1,000". The amount the customer is
 * actually charged.
 *
 * WHITELABEL: this used to be `formatMyr(cents)` — hardcoded `en-MY`, `MYR`,
 * and an unconditional `/100`. All three were wrong for a deployment selling
 * in another currency, and the `/100` was wrong in a way that shows a JPY
 * price 100× too small. The currency now comes from the API payload (each
 * plan and pack carries its own; the credits endpoint sends the deployment
 * default for custom amounts), and the exponent comes from
 * `@papan/types`' `formatCurrencyMinor`, which is shared with the server so
 * both sides agree on what a minor unit is.
 */
export { formatCurrencyMinor } from '@papan/types'

/**
 * A date rendered in the locale of the currency shown beside it — so a USD
 * screen reads M/D/YYYY and a MYR one D/M/YYYY, the date order matching the
 * amounts. The date sibling of `formatCurrencyMinor`; see its docblock in
 * `@papan/types` for why there is no maintained currency→locale map, and the
 * UTC-vs-Local note there for which of the pair to use: `…Date` (UTC) for a
 * midnight-UTC civil day (a term's effectiveFrom), `…DateLocal` for a
 * wall-clock instant (a Stripe period boundary, an invoice date).
 */
export { formatCurrencyDate, formatCurrencyDateLocal } from '@papan/types'

/*
 * `formatUsdMicro` USED TO LIVE HERE, and is deliberately gone.
 *
 * It rendered micro-units through a hardcoded `en-US` / `USD` `Intl` format,
 * for "AI metering only — never a charge on its own". After the money path was
 * re-denominated, its only two remaining callers were passing BILLING micro
 * into it: the customer's own promotions line and the console's campaign
 * revoke preview. On the MYR deployments that printed an RM30 promotional
 * grant as "$30.00", directly beneath a correctly-labelled "MYR 0.00" credit
 * balance on the same card.
 *
 * Both now use `formatBillingMicro` with a currency the server names. The
 * function is deleted rather than left unused because an unused USD-hardcoded
 * formatter in a whitelabel codebase is not neutral — it is the nearest
 * autocomplete hit for the next person formatting a micro amount, which is
 * exactly how it acquired both of the callers it should never have had.
 *
 * If a genuinely USD figure ever needs rendering — provider cost of goods is
 * the only real candidate — give it a name that says so (`formatProviderCostUsd`)
 * so the unit is legible at the call site rather than only in this file.
 */

/**
 * Billing-currency MICRO units → a formatted amount in that currency.
 *
 * The prepaid pool, its ledger and its threshold are all billing micro since
 * P8/P9, and none of them may be rendered with a hardcoded symbol — `$` on a
 * whitelabel deployment prints an RM50 balance as "$50.00". A hardcoded symbol
 * is the same class of defect as a hardcoded `/100`: silent, plausible, and
 * wrong by whatever the deployment actually charges in.
 *
 * `Intl` places the symbol and picks the fraction digits from the currency's own
 * ISO exponent, so a JPY deployment renders whole yen and a BHD one three
 * decimals without a special case here.
 */
export function formatBillingMicro(micro: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(micro / 1_000_000)
}

/**
 * Bytes → a compact storage label ("5 GB", "1 TB", "1.2 MB").
 *
 * Null/zero returns null so the caller drops the row entirely rather than
 * printing "0 GB" for a tenant that has no quota at all.
 *
 * ── Why it does not stop at GB ──────────────────────────────────────────
 *
 * It used to divide straight to GB, which is fine for the plan-picker bullets
 * it was written for — a plan's quota is a round number of gigabytes. It is
 * NOT fine for the current-plan card, which shows the TENANT's enforced quota:
 * an operator can negotiate any figure, and this deployment has a tenant on
 * 1,288,490 bytes. That rendered as **"0.0 GB"** — indistinguishable from "no
 * storage" on a tenant which in fact has ~1.2 MB and is actively enforcing it.
 *
 * A quota small enough to round to zero is exactly the one a customer most
 * needs stated, so the scale follows the value rather than the other way
 * round. Binary units (1024) throughout, matching the enforcer's own
 * arithmetic — `assertStorageCapacity` compares raw bytes, so a decimal
 * "MB" here would disagree with the byte figure Settings → Storage shows.
 */
export function formatStorage(bytes: number | null): string | null {
  if (bytes == null || bytes <= 0) return null
  const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const
  let value = bytes
  let unit = 0
  // Step up while there is a larger unit AND the value still fills it, so
  // 1,288,490 lands on MB rather than being forced into GB.
  //
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  // ROUNDING CAN PUSH IT BACK OVER THE BOUNDARY the loop just cleared.
  //
  // The loop compares the UNROUNDED value, but the label below rounds to one
  // decimal — so 1 GiB − 1 byte is 1023.999… MB, which fails `>= 1024` and then
  // PRINTS as "1024.0 MB": a unit the loop had just decided it did not fill.
  // One more promotion when the ROUNDED label would overflow gives "1.0 GB",
  // which is correct and is what a reader expects one byte below a boundary.
  if (unit < UNITS.length - 1 && Number(value.toFixed(1)) >= 1024) {
    value /= 1024
    unit += 1
  }
  // Whole numbers print bare ("5 GB"); anything else keeps one decimal, which
  // is enough to separate 1.2 MB from 1.9 MB without implying byte precision.
  return `${Number.isInteger(value) ? value : value.toFixed(1)} ${UNITS[unit]}`
}

/**
 * A typed major-unit amount ("100", "99.50", "1,000") → minor units, or null
 * if it isn't a usable amount.
 *
 * WHITELABEL: replaces `parseMyrToCents`, which assumed two decimals. The
 * accepted precision now follows the currency (none for JPY, three for BHD),
 * so a JPY deployment can't have "1000" silently become ¥10.
 *
 * Deliberately strict about what it accepts and deliberately NOT the authority
 * on bounds — the server re-validates floor/ceiling, because this is the only
 * thing between a typo and a real card charge and a client check can be
 * skipped entirely. What this rules out is the class of input that would make
 * a bounds check meaningless: NaN, negatives, and sub-minor-unit precision
 * that would silently round into a different charge than the one displayed.
 *
 * Grouping separators are stripped (people paste "1,000"); anything else
 * non-numeric fails rather than being coerced, so "10O" is an error, not 10.
 */
export { parseMajorToMinor } from '@papan/types'

/** A percentage clamped to 0–100 for meter widths (a cap of 0 reads as full). */
export function meterPct(used: number, cap: number): number {
  if (cap <= 0) return used > 0 ? 100 : 0
  return Math.max(0, Math.min(100, (used / cap) * 100))
}
